import React, { useEffect, useRef, useState } from 'react';

export default function ScrollReveal({
  children,
  variant = 'fade-up', // fade-up, fade-down, fade-left, fade-right, scale-up, fade-in
  duration = 800,
  delay = 0,
  threshold = 0.1,
  className = '',
  instant = false,
}) {
  const ref = useRef(null);
  const [isIntersecting, setIsIntersecting] = useState(instant);

  useEffect(() => {
    if (instant) {
      setIsIntersecting(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          // Once it reveals, we unobserve to keep it visible and avoid re-triggering
          if (ref.current) observer.unobserve(ref.current);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before element enters viewport
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  const getVariantStyles = () => {
    switch (variant) {
      case 'fade-up':
        return isIntersecting
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-12';
      case 'fade-down':
        return isIntersecting
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 -translate-y-12';
      case 'fade-left':
        return isIntersecting
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 translate-x-12';
      case 'fade-right':
        return isIntersecting
          ? 'opacity-100 translate-x-0'
          : 'opacity-0 -translate-x-12';
      case 'scale-up':
        return isIntersecting
          ? 'opacity-100 scale-100'
          : 'opacity-0 scale-95';
      case 'fade-in':
      default:
        return isIntersecting ? 'opacity-100' : 'opacity-0';
    }
  };

  const style = {
    transitionDuration: `${duration}ms`,
    transitionDelay: `${delay}ms`,
  };

  return (
    <div
      ref={ref}
      className={`transition-all ease-out ${getVariantStyles()} ${className}`}
      style={style}
    >
      {children}
    </div>
  );
}
